import { useEffect, useMemo, useRef, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import { useNonReactiveCallback } from "../useNonReactiveCallback";

export function useDebouncedMutation<T>(
  remoteValue: T,
  onMutation: (val: T) => any,
  debounceTime = 300,
  opts?: {
    maxWait?: number;
    leading?: boolean;
    trailing?: boolean;
    getValueKey?: (value: T) => string | undefined;
  },
  onSetLocalValue?: (val: T) => void
): [T, (val: T) => void, boolean] {
  const [localValue, setLocalValue] = useState(remoteValue);
  const flushed = useRef(true);
  const debounced = useDebouncedCallback(
    (val) => {
      flushed.current = true;
      onMutation(val as T);
    },
    debounceTime,
    opts
  );

  useEffect(
    () => () => {
      debounced.flush();
    },
    [debounced]
  );

  const remoteDependency = opts?.getValueKey
    ? opts.getValueKey(remoteValue)
    : remoteValue;
  const localDependency = opts?.getValueKey
    ? opts.getValueKey(localValue)
    : localValue;

  const updateLocalValue = useNonReactiveCallback(
    (remoteDep: typeof remoteDependency) => {
      if (remoteDep !== localDependency && flushed.current) {
        setLocalValue(remoteValue);
      }
    }
  );

  useEffect(() => {
    updateLocalValue(remoteDependency);
  }, [remoteDependency, updateLocalValue]);

  const setVal = (val: T) => {
    onSetLocalValue?.(val);
    flushed.current = false;
    debounced(val);
    setLocalValue(val);
    return undefined;
  };

  const dirty = useMemo(
    () => remoteDependency !== localDependency,
    [localDependency, remoteDependency]
  );

  return [localValue, setVal, dirty];
}
